import {
  useState,
  useEffect,
  SetStateAction
} from "react";
import { toast } from "react-toastify";
import * as H from "history";

import {
  CreateEditPromotionalTag,
  CreatePromotionalTagParams,
  PromotionalTagRequestName,
  UpdatePromotionalTagParams,
  usePromotionalTagApi
} from "../api/promotional-tags.api";

export interface UsePromotionalTagReturn {
  isLoading: boolean;
  promotionalTag: CreateEditPromotionalTag | null;
  setCurrentPromotionalTag: React.Dispatch<SetStateAction<CreateEditPromotionalTag | null>>
  createPromotionalTagReq: (promotionalTag: CreatePromotionalTagParams) => void;
  updatePromotionalTagReq: (data: UpdatePromotionalTagParams) => void;
}

const initialStateForCreate = {
  label: "",
  color: "",
  priority: 0
};

/**
 * Custom hook used to manage local promotional tag state as well as all API interactions
 */
const usePromotionalTag = (history: H.History, promotionalTagId?: number): UsePromotionalTagReturn => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ getProductsByPromotionalTagRes, getProductsByPromotionalTagReq ] = usePromotionalTagApi(PromotionalTagRequestName.GET_PRODUCTS_BY_PROMOTIONAL_TAG);
  const [ createPromotionalTagRes, createPromotionalTagReq ] = usePromotionalTagApi(PromotionalTagRequestName.CREATE_PROMOTIONAL_TAG);
  const [ updatePromotionalTagRes, updatePromotionalTagReq ] = usePromotionalTagApi(PromotionalTagRequestName.UPDATE_PROMOTIONAL_TAG);

  // if promotional tag id is provided, wait for get request to complete which will set currentPromotionalTag value
  const [ currentPromotionalTag, setCurrentPromotionalTag ] = useState<CreateEditPromotionalTag | null>(
    () => !promotionalTagId ? initialStateForCreate : null
  );

  // fetch promotional tag if id provided
  useEffect(() => {
    if (promotionalTagId) {
      getProductsByPromotionalTagReq({ id: promotionalTagId });
    }
  }, [ promotionalTagId, getProductsByPromotionalTagReq ]);

  // handle create API response
  useEffect(() => {
    if (createPromotionalTagRes.data) {
      setCurrentPromotionalTag(createPromotionalTagRes.data);
      toast.success("Successfully created promotional tag.");
      history.push(`/admin/promotions/promotional-tags/${createPromotionalTagRes.data.id}`);
    }

    if (createPromotionalTagRes.error) {
      toast.error(createPromotionalTagRes.error.message || "Unable to create promotional tag.");
    }
  }, [ createPromotionalTagRes, history ]);

  // update local state on update API response
  useEffect(() => {
    if (updatePromotionalTagRes.data) {
      setCurrentPromotionalTag(updatePromotionalTagRes.data);
      toast.success("Successfully updated promotional tag.");
    }

    if (updatePromotionalTagRes.error) {
      toast.error(updatePromotionalTagRes.error.message || "Unable to update promotional tag.");
    }
  }, [ updatePromotionalTagRes ]);

  // update local state when getting promotionalTag
  useEffect(() => {
    if (getProductsByPromotionalTagRes.data) {
      setCurrentPromotionalTag(getProductsByPromotionalTagRes.data);
    }

    if (getProductsByPromotionalTagRes.error) {
      toast.error(getProductsByPromotionalTagRes.error.message || "Unable to get promotional tag.");
    }
  }, [ getProductsByPromotionalTagRes ]);

  // set loading state if any of the requests status updated
  useEffect(() => {
    setIsLoading(getProductsByPromotionalTagRes.pending ||
      createPromotionalTagRes.pending ||
      updatePromotionalTagRes.pending);
  }, [
    updatePromotionalTagRes,
    createPromotionalTagRes,
    getProductsByPromotionalTagRes
  ]);

  return {
    isLoading,
    promotionalTag: currentPromotionalTag,
    setCurrentPromotionalTag,
    createPromotionalTagReq,
    updatePromotionalTagReq
  };
};

export default usePromotionalTag;
